import { Button } from "@/components/ui/button";
import { LoaderCircle, AlertCircle, Search, ChevronLeft, ChevronRight, Hash, Clock, List, LayoutGrid, FileText, ArrowRight, Copy, ChevronDown, ChevronUp, X } from "lucide-react";
import { useSearch, useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { searchBlockchain, getTransactionDetails } from "@/lib/bitcoin-api";
import { Block, DetailedTransaction } from "@shared/schema";
import { formatDate, formatBitcoin } from "@/lib/utils";
import BlockHashSearchBar from "@/components/BlockHashSearchBar";
import BlockOverview from "@/components/BlockOverview";
import BlockDetails from "@/components/BlockDetails";
import TransactionSummary from "@/components/TransactionSummary";
import React from "react";
import { FavoritesButton } from "@/components/favourites";
import { useToast } from "@/hooks/use-toast";

type BlockDataResponse = {
  txs: {
    id: string;
    events: ({
      type: 'utxo_input';
      source: string;
      amount: number;
      transaction_id: string;
    } | {
      type: 'utxo_output';
      destination: string;
      amount: number;
      transaction_id: string;
    })[];
  }[]
  transactions: {
    txid: string;
    version: number;
    size: number;
    locktime: number;
    weight: number;
    fee: number;
    status: {
      confirmed: boolean;
      block_height: number;
      block_hash: string;
      block_time: number;
    };
    vin: {
      txid: string;
      vout: number;
      prevout: {
        scriptpubkey: string;
        scriptpubkey_asm: string;
        scriptpubkey_type: string;
        scriptpubkey_address: string;
        value: number;
      };
      is_coinbase: boolean;
      sequence: number;
    }[];
    vout: {
      scriptpubkey: string;
      scriptpubkey_asm: string;
      scriptpubkey_type: string;
      scriptpubkey_address: string;
      value: number;
    }[];
  }[];
}

export default function BlockHashSearchPage() {
  const search = useSearch();
  const [_, setLocation] = useLocation();
  const searchParams = new URLSearchParams(search);
  const { toast } = useToast();

  const query = searchParams.get("query") || "";
  const searchType = searchParams.get("searchType") || "block_hash";
  const currentPage = searchParams.get("page") ? parseInt(searchParams.get("page") as string) : 1;
  const itemsPerPage = searchParams.get("limit") ? parseInt(searchParams.get("limit") as string) : 100;
  
  // Local pagination state for transactions display
  const [txPage, setTxPage] = React.useState(1);
  const txPerPage = 10;

  // State for expanded transaction rows
  const [expandedTxs, setExpandedTxs] = React.useState<Set<string>>(new Set());

  // State for input/output search in expanded transactions
  const [inputSearch, setInputSearch] = React.useState<{[txid: string]: string}>({});
  const [outputSearch, setOutputSearch] = React.useState<{[txid: string]: string}>({});

  // State for transaction list search by wallet address
  const [transactionSearch, setTransactionSearch] = React.useState("");

  const shouldSearch = (searchType === "block_hash" && query.length === 64) || 
                      (searchType === "block_height" && !isNaN(Number(query)) && query !== "");

  const queryKey = shouldSearch
    ? ["/api/blockchain/search", searchType, query, currentPage, itemsPerPage]
    : ["empty-query"];

  const {
    data: searchData,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldSearch) return Promise.resolve(null);
      const data = await searchBlockchain(searchType as "block_hash" | "block_height", query, currentPage, itemsPerPage, false);
      return data;
    },
    enabled: shouldSearch,
    staleTime: Infinity,
  });

  const isBlockData = searchData && "id" in searchData && ("height" in searchData || "number" in searchData);
  const blockData = isBlockData ? (searchData as Block) : null;

  // Extract transactions from data
  const transactions = blockData?.transactions || [];
  const txsFromBlockdaemon = blockData?.txs?.map((tx: any) => tx.id) || [];
  const allTransactions = transactions.length > 0 ? transactions : txsFromBlockdaemon;

  // Calculate input and output totals from blockData if available
  const calculateBlockTotals = () => {
    const blockDataAny = blockData as any;
    if (!blockDataAny) return { inputTotal: 0, outputTotal: 0, inputCount: 0, outputCount: 0 };

    let inputTotal = 0;
    let outputTotal = 0;
    let inputCount = 0;
    let outputCount = 0;

    blockDataAny.transactions?.forEach((tx: any) => {
      // Count and sum inputs
      if (tx.vin && Array.isArray(tx.vin)) {
        inputCount += tx.vin.length;
        tx.vin.forEach((input: any) => {
          if (input.prevout?.value && !input.is_coinbase) {
            inputTotal += (input.prevout.value * 100_000_000); // Amount is in satoshis (8 decimal places)
          }
        });
      }

      // Count and sum outputs
      if (tx.vout && Array.isArray(tx.vout)) {
        outputCount += tx.vout.length;
        tx.vout.forEach((output: any) => {
          if (output.value) {
            outputTotal += (output.value * 100_000_000); // Amount is in satoshis (8 decimal places)
          }
        });
      }
    });

    return { inputTotal, outputTotal, inputCount, outputCount };
  };

  const blockTotals = calculateBlockTotals();

  // Reset pagination when block data changes
  React.useEffect(() => {
    setTxPage(1);
  }, [blockData?.id, transactionSearch]);

  React.useEffect(() => {
    if (searchData && query) {
      sessionStorage.setItem("lastBlockSeerSearch", window.location.search);
    }
  }, [searchData, query]);

  // Pagination calculations
  const filteredAllTransactions = React.useMemo(() => {
    if (!blockData) return [];

    const blockDataResponse = blockData as any as BlockDataResponse;
    const searchLower = transactionSearch.toLowerCase().trim();
    return blockDataResponse.transactions.filter((tx) => {
      if (!tx.vin) return false;

      const matchesTxId = tx.txid.toLowerCase().includes(searchLower);
      
      // Check if search matches any input address
      const matchesInput = tx.vin.some((input: any) => 
        input.prevout?.scriptpubkey_address?.toLowerCase().includes(searchLower)
      );

      // Check if search matches any output address
      const matchesOutput = tx.vout.some((output: any) => 
        output.scriptpubkey_address?.toLowerCase().includes(searchLower)
      );
      
      // Check if search matches transaction ID
      const matchesInputTxId = tx.vin.some((input: any) => 
        input.prevout?.scriptpubkey_address?.toLowerCase().includes(searchLower)
      );

      const matchesOutputTxId = tx.vout.some((output: any) => 
        output.scriptpubkey_address?.toLowerCase().includes(searchLower)
      );
      
      return matchesInput || matchesOutput || matchesInputTxId || matchesOutputTxId || matchesTxId;
    });
  }, [transactionSearch, blockData]);

  const totalTxPages = Math.ceil(filteredAllTransactions.length / txPerPage);
  const startIndex = (txPage - 1) * txPerPage;
  const endIndex = startIndex + txPerPage;

  const currentTransactions = filteredAllTransactions.slice(startIndex, endIndex);

  // Fetch detailed transaction data for current page transactions
  // const transactionQueries = useQuery({
  //   queryKey: ['block-transactions', blockData?.id, currentTransactions],
  //   queryFn: async () => {
  //     if (!currentTransactions.length) return [];
      
  //     const results = await Promise.allSettled(
  //       currentTransactions.map(async (txid) => {
  //         try {
  //           const txDetails = await getTransactionDetails(txid, true); // Skip block data for performance
  //           return txDetails;
  //         } catch (error) {
  //           console.error(`Failed to fetch transaction ${txid}:`, error);
  //           return null;
  //         }
  //       })
  //     );
      
  //     return results.map((result, index) => ({
  //       txid: currentTransactions[index],
  //       details: result.status === 'fulfilled' ? result.value : null
  //     }));
  //   },
  //   enabled: !!blockData && currentTransactions.length > 0,
  //   staleTime: Infinity,
  // });

  // Filter transactions based on wallet address search
  // const filteredTransactionData = React.useMemo(() => {
  //   if (!transactionQueries.data || !transactionSearch.trim()) {
  //     return transactionQueries.data || [];
  //   }

  //   const searchLower = transactionSearch.toLowerCase().trim();
    
  //   return transactionQueries.data.filter((tx) => {
  //     if (!tx.details) return false;
      
  //     // Check if search matches any input address
  //     const matchesInput = tx.details.vin.some(input => 
  //       input.prevout?.scriptpubkey_address?.toLowerCase().includes(searchLower)
  //     );
      
  //     // Check if search matches any output address
  //     const matchesOutput = tx.details.vout.some(output => 
  //       output.scriptpubkey_address?.toLowerCase().includes(searchLower)
  //     );
      
  //     // Check if search matches transaction ID
  //     const matchesTxId = tx.txid.toLowerCase().includes(searchLower);
      
  //     return matchesInput || matchesOutput || matchesTxId;
  //   });
  // }, [transactionQueries.data, transactionSearch]);

  const handlePrevPage = () => {
    if (txPage > 1) {
      setTxPage(txPage - 1);
    }
  };

  const handleNextPage = () => {
    if (txPage < totalTxPages) {
      setTxPage(txPage + 1);
    }
  };

  // Helper function to get the first input address
  const getFromAddress = (tx: DetailedTransaction): string => {
    if (tx.vin.length > 0 && tx.vin[0].prevout?.scriptpubkey_address) {
      return tx.vin[0].prevout.scriptpubkey_address || "Unknown";
    }
    return "Unknown";
  };

  // Helper function to truncate addresses longer than 40 characters
  const truncateAddress = (address: string): string => {
    if (address.length <= 20) {
      return address;
    }
    return `${address.slice(0, 10)}...${address.slice(-10)}`;
  };

  // Helper function to get output display
  const getToDisplay = (tx: DetailedTransaction): string => {
    const validOutputs = tx.vout.filter(output => output.scriptpubkey_address);
    
    if (validOutputs.length === 0) {
      return "No outputs";
    } else if (validOutputs.length === 1) {
      return validOutputs[0].scriptpubkey_address || "Unknown";
    } else {
      return `${validOutputs.length} Outputs`;
    }
  };

  // Helper function to calculate total output amount
  const getTotalAmount = (tx: DetailedTransaction): number => {
    return tx.vout.reduce((sum, output) => sum + output.value, 0);
  };

  // Copy text to clipboard with toast notification
  const copyToClipboard = (text: string, type: 'address' | 'txid' | 'amount') => {
    navigator.clipboard.writeText(text);
    
    const messages = {
      address: {
        title: "Copied to clipboard",
        description: "Address has been copied to your clipboard."
      },
      txid: {
        title: "Copied to clipboard", 
        description: "Transaction ID has been copied to your clipboard."
      },
      amount: {
        title: "Copied to clipboard",
        description: "Amount has been copied to your clipboard."
      }
    };

    toast({
      title: messages[type].title,
      description: messages[type].description,
    });
  };

  const handleRetrySearch = () => {
    if (shouldSearch) {
      refetch();
    }
  };

  const handleModifySearch = () => {
    setLocation("/search");
  };

  const handleSearchUpdate = (values: { searchType: "block_hash" | "block_height"; query: string }) => {
    const params = new URLSearchParams();
    params.set("query", values.query.trim());
    
    if (values.searchType === "block_hash") {
      setLocation(`/search/block-hash?${params.toString()}`);
    } else {
      setLocation(`/search/block-height?${params.toString()}`);
    }
  };

  // Helper function to create default name for the favorite
  const createDefaultFavoriteName = (hash: string) => {
    return `Block Hash: ...${hash.slice(-8)}`;
  };

  // Toggle transaction expansion
  const toggleTxExpansion = (txid: string) => {
    const newExpanded = new Set(expandedTxs);
    if (newExpanded.has(txid)) {
      newExpanded.delete(txid);
    } else {
      newExpanded.add(txid);
    }
    setExpandedTxs(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col flex-grow min-h-screen">
          <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4 w-full">
            <div className="bg-white dark:bg-[#161B22] shadow-sm dark:shadow-lg dark:shadow-black/20 rounded-xl p-8 border border-slate-200 dark:border-gray-800 flex flex-col items-center justify-center py-12 mt-4 animate-pulse-subtle">
              <LoaderCircle className="h-12 w-12 animate-spin text-blue-500 dark:text-blue-400" />
              <p className="mt-4 text-slate-700 dark:text-gray-300 font-medium">Loading blockchain data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError && queryError) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col flex-grow min-h-screen">
          <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4 w-full">
            <div className="bg-white dark:bg-[#161B22] shadow-sm dark:shadow-lg dark:shadow-black/20 rounded-lg p-6 border border-slate-200 dark:border-gray-800 mt-6">
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 mb-4">
                  <AlertCircle className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Error loading block</h3>
                <Button onClick={handleRetrySearch} className="dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white">
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blockData) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col flex-grow min-h-screen">
          <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4 w-full">
            <div className="mt-2 pt-0">
              <div className="mb-6 flex items-center">
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Extract block data with API variations (only if blockData exists)
  const blockDataAny = blockData as any;
  const blockHeight = blockData?.height || blockData?.number || blockDataAny?.data?.number || 0;
  const blockTime = blockData?.timestamp || blockData?.date || blockDataAny?.data?.date || 0;
  const txCount = blockData?.tx_count || blockData?.num_txs || blockDataAny?.data?.num_txs || 0;
  const previousBlockHash = blockData?.previousblockhash || blockData?.parent_id || blockDataAny?.data?.parent_id;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-col flex-grow min-h-screen">
        <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4 w-full">
          <div className="space-y-4">
            <div className="mt-2 pt-0">
              <div className="mb-6 flex items-center justify-end">
                <FavoritesButton
                  enabled={shouldSearch && !!query && !!blockData}
                  createDefaultName={createDefaultFavoriteName}
                />
              </div>

              {/* Block Hash Search Bar */}
              <div className="mb-6">
                <BlockHashSearchBar 
                  onUpdate={handleSearchUpdate} 
                  defaultValues={{
                    query: query,
                  }}
                />
              </div>

              {/* Block Details Components */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Block Overview Component */}
                <div className="bg-white dark:bg-[#161B22] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-gray-800">
                  <BlockOverview 
                    blockHeight={blockHeight}
                    blockTime={blockTime}
                  />
                </div>

                {/* Block Details Component */}
                <div className="bg-white dark:bg-[#161B22] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-gray-800">
                  <BlockDetails
                    blockId={blockData.id}
                    blockHeight={blockHeight}
                    previousBlockHash={previousBlockHash}
                    txCount={txCount}
                    blockTime={blockTime}
                  />
                </div>

                {/* Transaction Summary Component */}
                <div className="bg-white dark:bg-[#161B22] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-gray-800">
                  <TransactionSummary
                    inputCount={blockTotals.inputCount}
                    outputCount={blockTotals.outputCount}
                    inputTotal={blockTotals.inputTotal}
                    outputTotal={blockTotals.outputTotal}
                  />
                </div>
              </div>

              {/* Block Transactions */}
              <div className="w-full">
                <div className="bg-white dark:bg-[#161B22] rounded-xl shadow-sm border border-slate-200 dark:border-gray-800">
                  {/* Header */}
                  <div className="p-6 border-b border-slate-200 dark:border-gray-800">
                    <div className="flex flex-col gap-4">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Block Transactions</h3>
                      
                      {/* Transaction Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="text"
                          placeholder="Search by wallet address or transaction ID..."
                          value={transactionSearch}
                          onChange={(e) => setTransactionSearch(e.target.value)}
                          className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {transactionSearch && (
                          <button
                            type="button"
                            onClick={() => setTransactionSearch("")}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Clear search"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Search Results Info */}
                      {transactionSearch.trim() && (
                        <div className="text-sm text-slate-600 dark:text-gray-400">
                          {filteredAllTransactions.length > 0 ? (
                            `Found ${filteredAllTransactions.length} matching transaction${filteredAllTransactions.length === 1 ? '' : 's'}`
                          ) : (
                            'No transactions match your search'
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Transaction List */}
                  <div className="divide-y divide-slate-200 dark:divide-gray-800">
                    {allTransactions.length > 0 ? (
                      isLoading ? (
                        <div className="p-8 text-center">
                          <LoaderCircle className="h-8 w-8 animate-spin text-blue-500 dark:text-blue-400 mx-auto mb-4" />
                          <p className="text-slate-500 dark:text-gray-400">Loading transaction details...</p>
                        </div>
                      ) : (
                        currentTransactions?.map((tx, index) => {
                          const txDetails = tx;
                          const fromAddress = txDetails ? getFromAddress(txDetails) : "Unknown";
                          const isExpanded = expandedTxs.has(tx.txid);
                          
                          return (
                            <div key={tx.txid}>
                              <div className="p-4 hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors">
                                <div className="grid grid-cols-12 gap-4 items-center">
                                  {/* Column 1: TX ID and Time */}
                                  <div className="col-span-4">
                                    <div className="flex items-center">
                                      <div className="flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full font-medium text-xs mr-3 flex-shrink-0">
                                        {startIndex + index + 1}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center">
                                          <Link to={`/search/transaction?query=${tx.txid}`}>
                                            <span className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline cursor-pointer truncate">
                                              {tx.txid.slice(0, 16)}...
                                            </span>
                                          </Link>
                                          <button 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              copyToClipboard(tx.txid, 'txid');
                                            }} 
                                            className="ml-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                            title="Copy transaction ID"
                                          >
                                            <Copy className="h-3 w-3" />
                                          </button>
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-gray-400">
                                          {txDetails?.status.block_time ? 
                                            formatDate(txDetails.status.block_time, "MM/DD HH:mm") : 
                                            formatDate(blockTime, "MM/DD HH:mm")
                                          }
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Column 2: From and To */}
                                  <div className="col-span-4">
                                    {txDetails ? (
                                      <div className="space-y-1">
                                        <div className="flex items-center text-sm">
                                          <span className="text-slate-500 dark:text-gray-400 text-xs mr-2">From</span>
                                          {fromAddress !== "Unknown" && fromAddress.toLowerCase() !== "coinbase" ? (
                                            <Link to={`/search/address?query=${fromAddress}`}>
                                              <span className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline cursor-pointer truncate">
                                                {fromAddress.slice(0, 25)}...
                                              </span>
                                            </Link>
                                          ) : (
                                            <span className="font-mono text-slate-700 dark:text-gray-300 truncate">
                                              {fromAddress.slice(0, 25)}...
                                            </span>
                                          )}
                                          {fromAddress !== "Unknown" && fromAddress.toLowerCase() !== "coinbase" && (
                                            <button 
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                copyToClipboard(fromAddress, 'address');
                                              }} 
                                              className="ml-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                              title="Copy address"
                                            >
                                              <Copy className="h-3 w-3" />
                                            </button>
                                          )}
                                        </div>
                                        <div className="flex items-center text-sm">
                                          <span className="text-slate-500 dark:text-gray-400 text-xs mr-2">To</span>
                                          {txDetails.vout.length === 1 && txDetails.vout[0].scriptpubkey_address ? (
                                            <>
                                              <Link to={`/search/address?query=${txDetails.vout[0].scriptpubkey_address}`}>
                                                <span className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline cursor-pointer truncate">
                                                  {txDetails.vout[0].scriptpubkey_address.slice(0, 25)}...
                                                </span>
                                              </Link>
                                              <button 
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  copyToClipboard(txDetails.vout[0].scriptpubkey_address || "", 'address');
                                                }} 
                                                className="ml-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                title="Copy address"
                                              >
                                                <Copy className="h-3 w-3" />
                                              </button>
                                            </>
                                          ) : (
                                            <span className="font-mono text-slate-700 dark:text-gray-300 truncate">
                                              {getToDisplay(txDetails).length > 15 ? 
                                                getToDisplay(txDetails).slice(0, 25) + "..." : 
                                                getToDisplay(txDetails)
                                              }
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-sm text-slate-500 dark:text-gray-400">
                                        Loading...
                                      </div>
                                    )}
                                  </div>

                                  {/* Column 3: Amount, Fee and Expand Button */}
                                  <div className="col-span-4 text-right">
                                    <div className="flex items-center justify-end gap-3">
                                      {txDetails ? (
                                        <div className="space-y-1">
                                          <div className="flex items-center justify-end">
                                            <span className="text-sm font-medium text-slate-900 dark:text-white whitespace-nowrap">
                                              {formatBitcoin(getTotalAmount(txDetails))} BTC
                                            </span>
                                          </div>
                                          <div className="text-xs text-slate-500 dark:text-gray-400 whitespace-nowrap">
                                            Fee {formatBitcoin(txDetails.fee || 0)} BTC
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-sm text-slate-500 dark:text-gray-400">
                                          Loading...
                                        </div>
                                      )}
                                      <button
                                        onClick={() => toggleTxExpansion(tx.txid)}
                                        className="p-1 hover:bg-slate-200 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                                        title={isExpanded ? "Collapse details" : "Expand details"}
                                      >
                                        {isExpanded ? (
                                          <ChevronUp className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Expandable Details Section */}
                              {isExpanded && txDetails && (
                                <div className="bg-slate-50 dark:bg-gray-900/50 border-t border-slate-200 dark:border-gray-800">
                                  <div className="p-6">
                                    <div className="flex items-start gap-8">
                                      {/* Inputs */}
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between gap-2 mb-4">
                                          <span className="text-slate-900 dark:text-white font-medium">Input ({txDetails.vin.length})</span>
                                          <span className="text-slate-900 dark:text-white whitespace-nowrap">
                                            {formatBitcoin(txDetails.vin.reduce((sum, input) => sum + (input.prevout?.value || 0), 0))} BTC
                                          </span>
                                        </div>
                                        
                                        {/* Input Search */}
                                        <div className="relative mb-3">
                                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                          <input
                                            type="text"
                                            placeholder="Search inputs..."
                                            value={inputSearch[tx.txid] || ""}
                                            onChange={(e) => setInputSearch(prev => ({ ...prev, [tx.txid]: e.target.value }))}
                                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          />
                                        </div>
                                        
                                        <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                                          {txDetails.vin
                                            .filter(input => 
                                              !inputSearch[tx.txid] || 
                                              (input.prevout?.scriptpubkey_address || "").toLowerCase().includes((inputSearch[tx.txid] || "").toLowerCase()) ||
                                              input.is_coinbase
                                            )
                                            .map((input, inputIndex) => (
                                            <div key={inputIndex} className="flex items-center justify-between bg-slate-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                                              <div className="flex-1 px-3 py-2">
                                                <div className="flex items-center">
                                                  {input.is_coinbase ? (
                                                    <span className="text-slate-900 dark:text-white font-mono text-sm">
                                                      Coinbase
                                                    </span>
                                                  ) : (
                                                    <>
                                                      <Link to={`/search/address?query=${input.prevout?.scriptpubkey_address}`} className="text-blue-600 dark:text-blue-400 font-mono text-sm truncate cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                                                        {truncateAddress(input.prevout?.scriptpubkey_address || "Unknown")}
                                                      </Link>
                                                      {input.prevout?.scriptpubkey_address && (
                                                        <button 
                                                          onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            copyToClipboard(input.prevout?.scriptpubkey_address || "", 'address');
                                                          }} 
                                                          className="ml-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                          title="Copy address"
                                                        >
                                                          <Copy className="h-3 w-3" />
                                                        </button>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="px-3 py-2">
                                                <span className="text-slate-900 dark:text-white font-mono text-sm whitespace-nowrap">
                                                  {formatBitcoin(input.prevout?.value || 0)} BTC
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Arrow */}
                                      <div className="flex items-center justify-center pt-8">
                                        <ArrowRight className="h-6 w-6 text-blue-500" />
                                      </div>

                                      {/* Outputs */}
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between gap-2 mb-4">
                                          <span className="text-slate-900 dark:text-white font-medium">Output ({txDetails.vout.length})</span>
                                          <span className="text-slate-900 dark:text-white whitespace-nowrap">
                                            {formatBitcoin(txDetails.vout.reduce((sum, output) => sum + output.value, 0))} BTC
                                          </span>
                                        </div>
                                        
                                        {/* Output Search */}
                                        <div className="relative mb-3">
                                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                          <input
                                            type="text"
                                            placeholder="Search outputs..."
                                            value={outputSearch[tx.txid] || ""}
                                            onChange={(e) => setOutputSearch(prev => ({ ...prev, [tx.txid]: e.target.value }))}
                                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          />
                                        </div>
                                        
                                        <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                                          {txDetails.vout
                                            .filter(output => 
                                              !outputSearch[tx.txid] || 
                                              (output.scriptpubkey_address || "").toLowerCase().includes((outputSearch[tx.txid] || "").toLowerCase())
                                            )
                                            .map((output, outputIndex) => (
                                            <div key={outputIndex} className="bg-slate-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                                              <div className="flex items-center justify-between px-3 py-2">
                                                <div className="flex items-center">
                                                  <Link to={`/search/address?query=${output.scriptpubkey_address}`} className="text-blue-600 dark:text-blue-400 font-mono text-sm truncate cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                                                    {truncateAddress(output.scriptpubkey_address || "No Address (OP_RETURN)")}
                                                  </Link>
                                                  {output.scriptpubkey_address && (
                                                    <button 
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        copyToClipboard(output.scriptpubkey_address || "", 'address');
                                                      }} 
                                                      className="ml-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                      title="Copy address"
                                                    >
                                                      <Copy className="h-3 w-3" />
                                                    </button>
                                                  )}
                                                </div>
                                                <span className="text-slate-900 dark:text-white font-mono text-sm ml-4 whitespace-nowrap">
                                                  {formatBitcoin(output.value)} BTC
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )
                    ) : (
                      <div className="p-8 text-center text-slate-500 dark:text-gray-400">
                        <List className="h-12 w-12 mx-auto mb-4 text-slate-300 dark:text-gray-600" />
                        <p>No transactions available for this block</p>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalTxPages > 1 && (
                    <div className="p-4 border-t border-slate-200 dark:border-gray-800 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={txPage === 1}
                          onClick={handlePrevPage}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={txPage === totalTxPages}
                          onClick={handleNextPage}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-slate-500 dark:text-gray-400">
                          {`Showing ${startIndex + 1}-${Math.min(endIndex, filteredAllTransactions.length)} of ${filteredAllTransactions.length} transactions`}
                        </span>
                        <span className="text-sm text-slate-500 dark:text-gray-400">
                          Page {txPage} of {totalTxPages}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Search Results Footer */}
                  {filteredAllTransactions.length > 0 && (
                    <div className="p-4 border-t border-slate-200 dark:border-gray-800 flex items-center justify-center">
                      <span className="text-sm text-slate-500 dark:text-gray-400">
                        {filteredAllTransactions.length > 0 ? (
                          `Showing all ${filteredAllTransactions.length} matching transaction${filteredAllTransactions.length === 1 ? '' : 's'}`
                        ) : (
                          'No transactions match your search criteria'
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
